import React from 'react'

import { Link } from 'gatsby'
import styled, { css } from 'styled-components'

import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors } from 'src/utilities/Colors'
import { ITypographyProps } from 'src/utilities/Interfaces'
import { FontFamily, Typescale } from 'src/utilities/Type'

const TypographyComponent = styled.div<{
  align?: string
  color?: string
  defaultStyles: ITypographyProps
  fontFamily?: string
  fontSize?: number
  fontSizeMobile?: number
  fontVariationSettings?: string
  fontWeight?: number
  letterSpacing?: string
  lineHeight?: number
  margin?: string
  marginBottom?: string
  marginTop?: string
  marginInline?: string
  textTransform?: string
  textWrapBalance?: boolean
  maxWidth?: string
  case?: string
}>`
  font-feature-settings: 'liga' 0, 'clig' 0;

  ${props =>
    props.align
      ? css`
          text-align: ${props.align};
        `
      : null}
  ${props =>
    props.letterSpacing
      ? css`
          letter-spacing: ${props.letterSpacing}em;
        `
      : null}
  letter-spacing: ${props =>
    props.letterSpacing
      ? props.letterSpacing
      : props.defaultStyles.letterSpacing};
  font-family: ${props =>
    props.fontFamily ? props.fontFamily : props.defaultStyles.fontFamily};
  font-variation-settings: ${props =>
    props.fontVariationSettings
      ? props.fontVariationSettings
      : props.defaultStyles.fontVariationSettings};
  font-size: ${props =>
    props.fontSize ? props.fontSize : props.defaultStyles.fontSize}rem;
  ${props =>
    props.fontSizeMobile
      ? css`
          @media (max-width: ${Breakpoints.sm}px) {
            font-size: ${props.fontSizeMobile}rem;
          }
        `
      : null}
  line-height: ${props =>
    props.lineHeight ? props.lineHeight : props.defaultStyles.lineHeight};
  color: ${props => (props.color ? props.color : props.defaultStyles.color)};
  font-weight: ${props =>
    props.fontWeight ? props.fontWeight : props.defaultStyles.fontWeight};
  ${props => (props.margin ? `margin: ${props.margin};` : 'margin: 0;')};
  ${props =>
    props.marginBottom ? `margin-bottom: ${props.marginBottom};` : ''};
  ${props => (props.marginTop ? `margin-top: ${props.marginTop};` : '')};
  ${props =>
    props.textTransform ? `text-transform: ${props.textTransform}` : null};
  ${props =>
    props.defaultStyles.textWrapBalance ? `text-wrap: balance` : null};
  ${props => (props.textWrapBalance ? `text-wrap: balance` : null)};
  ${props => (props.maxWidth ? `max-width: ${props.maxWidth}` : null)};
  ${props =>
    props.marginInline ? `margin-inline: ${props.marginInline}` : ''};
`

export const Description = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.pigeon800,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.description,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.54
  }
}))``

export const DescriptionSmall = styled(TypographyComponent as any).attrs(
  props => ({
    as: props.as ? props.as : 'p',
    defaultStyles: {
      color: BrandColors.pigeon700,
      fontFamily: FontFamily.brandPrimary,
      fontSize: Typescale.descriptionSmall,
      fontWeight: 400,
      letterSpacing: 'normal',
      lineHeight: 2
    }
  })
)``

export const Lead = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.lead,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.5
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.paragraph
}))``

export const LeadXL = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.leadXL,
    fontWeight: 400,
    lineHeight: 1.2
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.lead
}))``

export const ParagraphSmall = styled(TypographyComponent as any).attrs(
  props => ({
    as: props.as ? props.as : 'p',
    defaultStyles: {
      color: BrandColors.text,
      fontFamily: FontFamily.brandPrimary,
      fontSize: Typescale.paragraphSmall,
      fontWeight: 400,
      lineHeight: 1.5
    }
  })
)``

export const Paragraph = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.paragraph,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.43
  }
}))``

export const ParagraphLarge = styled(TypographyComponent as any).attrs(
  props => ({
    as: props.as ? props.as : 'p',
    defaultStyles: {
      color: BrandColors.text,
      fontFamily: FontFamily.brandPrimary,
      fontSize: Typescale.paragraphLarge,
      fontWeight: 400,
      letterSpacing: 'normal',
      lineHeight: 1.43
    }
  })
)``

export const LabelXL = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h3',
  defaultStyles: {
    color: BrandColors.pigeon800,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.labelXL,
    fontWeight: 400,
    letterSpacing: '0.1em',
    lineHeight: 1.5
  }
}))`
  text-transform: uppercase;
`

export const LabelLarge = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h3',
  defaultStyles: {
    color: BrandColors.pigeon800,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.labelLarge,
    fontWeight: 400,
    letterSpacing: '0.179em',
    lineHeight: 1.714
  }
}))`
  text-transform: uppercase;
`

export const Label = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h3',
  defaultStyles: {
    color: BrandColors.pigeon800,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.label,
    fontWeight: 400,
    letterSpacing: '0.125em',
    lineHeight: 1.273
  }
}))`
  text-transform: uppercase;
`

export const LabelTiny = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h4',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.labelTiny,
    fontWeight: 400,
    letterSpacing: '0.15em',
    lineHeight: 1.6
  }
}))`
  text-transform: uppercase;
`
const defaultLinkStyles = {
  color: BrandColors.text,
  fontFamily: FontFamily.brandPrimary,
  fontSize: Typescale.link,
  fontWeight: 400,
  letterSpacing: 'normal',
  lineHeight: 1.5
}

export const chevronRightSVG = (
  <svg height='10' width='6' xmlns='http://www.w3.org/2000/svg'>
    <g fill='none'>
      <path d='M-5-3h16v16H-5z' />
      <path
        d='M.529 1.471A.667.667 0 011.47.53l4 4a.667.667 0 01.02.921l-3.666 4a.667.667 0 01-.983-.9l3.235-3.53L.53 1.471z'
        fill='currentColor'
      />
    </g>
  </svg>
)

export const arrowRightSVG = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='12'
    height='12'
    fill='none'
    viewBox='0 0 12 12'
  >
    <g clipPath='url(#clip0_965_15506)'>
      <path
        fill='currentColor'
        d='M.584 6.5a.75.75 0 01.75-.75H9.38L7.478 3.848a.75.75 0 111.06-1.06L11.72 5.97a.747.747 0 010 1.06L8.54 10.212a.75.75 0 11-1.061-1.06L9.38 7.25H1.334a.75.75 0 01-.75-.75z'
      ></path>
    </g>
    <defs>
      <clipPath id='clip0_965_15506'>
        <path
          fill='currentColor'
          d='M0 0H12V12H0z'
          transform='matrix(-1 0 0 1 12.25 .5)'
        ></path>
      </clipPath>
    </defs>
  </svg>
)

export const linkCSS = css<{
  arrow?: boolean
  color?: string
  fontFamily?: string
  fontSize?: number
  fontWeight?: number
  hoverColor?: string
  lineHeight?: number
  textDecoration?: string
}>`
  color: ${props => (props.color ? props.color : defaultLinkStyles.color)};
  font-family: ${props =>
    props.fontFamily ? props.fontFamily : defaultLinkStyles.fontFamily};
  font-size: ${props =>
    props.fontSize ? props.fontSize : defaultLinkStyles.fontSize}rem;
  font-weight: ${props =>
    props.fontWeight ? props.fontWeight : defaultLinkStyles.fontWeight};
  line-height: ${props =>
    props.lineHeight ? props.lineHeight : defaultLinkStyles.lineHeight};
  position: relative;
  transition: all 0.3s;
  &:hover {
    color: ${props =>
      props.hoverColor ? props.hoverColor : BrandColors.primary};
  }
  ${props =>
    props.arrow
      ? css`
          svg {
            margin-left: 6px;
            transition: margin-left 0.2s, margin-right 0.2s;
          }
          &:hover svg {
            margin-left: 12px;
            margin-right: -6px;
          }
        `
      : null}
  ${props =>
    props.textDecoration ? `border-bottom: 1px solid ${props.color}` : null}
`

export const StyledLinkWrapper = styled(
  ({ arrow, hoverColor, lineHeight, className, ...props }) => (
    <Link className={className} {...props} />
  )
)<{
  arrow?: boolean
  color?: string
  fontFamily?: string
  fontSize?: number
  fontWeight?: number
  hoverColor?: string
  lineHeight?: number
  textDecoration?: string
}>`
  ${linkCSS}
`

export const StyledLink = props => (
  <StyledLinkWrapper {...props}>
    {props.children}
    {props.arrow && arrowRightSVG}
  </StyledLinkWrapper>
)

export const ExternalLinkWrapper = styled(TypographyComponent as any).attrs(
  props => ({
    as: 'a',
    defaultStyles: defaultLinkStyles
  })
)<{
  hoverColor?: string
}>`
  ${linkCSS}
`

export const ExternalLink = props => (
  <ExternalLinkWrapper {...props}>
    {props.children}
    {props.arrow && arrowRightSVG}
  </ExternalLinkWrapper>
)

export const DropdownLink = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'span',
  defaultStyles: defaultLinkStyles
}))`
  cursor: default;
  transition: all 0.3s;
  font-weight: 500;
  &:hover {
    color: ${({ color = BrandColors.text }) => `${color}CC`};
    &:after {
      background-color: ${({ color = BrandColors.text }) => `${color}CC`};
    }
  }
  &:after {
    content: '';
    height: 6px;
    width: 10.5px;
    background-size: cover;
    display: inline-block;
    background-color: ${({ color = BrandColors.text }) => color};
    mask-image: url(/img/chevron-down.svg);
    margin-left: 8px;
    margin-bottom: 1px;
    transition: all 0.3s;
    outline: none;
  }
`

const LinkSmallCSS = `
  font-size: ${Typescale.linkSmall}rem;
  line-height: 2;
`

export const LinkLargeCSS = `
  font-size: ${Typescale.linkLarge}rem;
  line-height: 1.2;
  font-weight: 400;
`

export const StyledLinkLarge = styled(StyledLink)`
  ${LinkLargeCSS}
`

export const ExternalLinkLarge = styled(ExternalLink as any)`
  ${LinkLargeCSS}
`

export const StyledLinkSmall = styled(StyledLink as any)`
  ${LinkSmallCSS}
`

export const ExternalLinkSmall = styled(ExternalLink as any)`
  ${LinkSmallCSS}
`

export const LinkXSCSS = `
  font-size: ${Typescale.linkXS}rem;
  line-height: 2;
  svg {
    transform: scale(0.8) translateY(1px);
  }
`

export const StyledLinkXS = styled(StyledLink as any)`
  ${LinkXSCSS}
`

export const ExternalLinkXS = styled(ExternalLink as any)`
  ${LinkXSCSS}
`

export const TitleXXL = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h2',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.titleXXL,
    fontWeight: 500,
    letterSpacing: 'normal',
    lineHeight: 1.2
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleXL
}))``

export const TitleXL = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h2',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.titleXL,
    fontWeight: 500,
    letterSpacing: 'normal',
    lineHeight: 1.2
  }
}))``

export const TitleLarge = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h3',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.titleLarge,
    fontWeight: 500,
    letterSpacing: 'normal',
    lineHeight: 1.2
  }
}))``

export const Title = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h4',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.title,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.2
  }
}))``

export const TitleSmall = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h5',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.titleSmall,
    fontWeight: 500,
    letterSpacing: 'normal',
    lineHeight: 1.5
  }
}))``

export const TitleXS = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h6',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.titleXS,
    fontWeight: 500,
    letterSpacing: 'normal',
    lineHeight: 1.5
  }
}))``

export const DisplaySmall = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h1',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.titleXL,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.188
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleLarge
}))``

export const Display = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h1',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.display,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.208
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleXXL
}))``

export const DisplayXXL = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h1',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.displayXXL,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.09
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.displaySmall
}))``

/*
 * New font components
 */
// Title/H1
// color: var(--White, #FFF);
// leading-trim: both;
// text-edge: cap;
// font-feature-settings: 'liga' off;
// font-family: Mikro;
// font-size: 3.75rem;
// font-style: normal;
// font-weight: 400;
// line-height: 4.5rem; /* 120% */
// letter-spacing: -0.075rem;
export const TitleH1 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h1',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.titleH1,
    fontWeight: 400,
    letterSpacing: '-0.075rem',
    lineHeight: '1.2',
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleH2
}))``

// Title/H2
// font-size: 2.25rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.1667;
// letter-spacing: -0.0225rem;
export const TitleH2 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h2',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.titleH2,
    fontWeight: 400,
    letterSpacing: '-0.0225rem',
    lineHeight: 1.1667,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleH3
}))``

// Title/H3
// font-size: 1.875rem;
// font-style: normal;
// font-weight: 400;
// line-height: 2.25rem;
// letter-spacing: -0.01875rem;
export const TitleH3 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h3',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimaryHeading,
    fontSize: Typescale.titleH3,
    fontWeight: 400,
    letterSpacing: '-0.01875rem',
    lineHeight: 1.2,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleH4
}))``

// Title/H4
// font-size: 1.25rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.75rem; /* 140% */
export const TitleH4 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h4',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.titleH4,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.4,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleH4
}))``

// Title/H5
// font-size: 1.125rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.5rem; /* 133.333% */
export const TitleH5 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h5',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.titleH5,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.33,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleH5
}))``

// Title/H6
// font-size: 1rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.4375rem;/* 143.75% */
export const TitleH6 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'h6',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.titleH6,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.43,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.titleH6
}))``

// Body/B1 -
// font-size: 1.5rem;
// font-style: normal;
// font-weight: 400;
// line-height: 2.125rem; /* 141.667% */
export const Body1 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.body1,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.41,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.body2
}))``

// Body/B2 -
// font-size: 1.25rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.75rem; /* 140% */
export const Body2 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.body2,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.4,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.body2
}))``

// Body/B2 Regular
export const Body2Regular = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.body2,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.4,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.body2
}))``

// Body/B3 - Base
// font-size: 1rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.375rem; /* 137.5% */
export const Body3 = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.body3,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.375,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.body3
}))``

// Caption
// font-size: 0.875rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.3125rem; /* 150% */
export const Caption = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.caption,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.5,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.caption
}))``

// CaptionSmall
// font-size: 0.6875rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.125rem; /* 163.636% */
export const CaptionSmall = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.captionSmall,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.6,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile
    ? props.fontSizeMobile
    : Typescale.caption
}))``

// Top Hat -
// font-size: 0.9375rem;
// font-style: normal;
// font-weight: 500;
// line-height: 1.3125rem; /* 140% */
// letter-spacing: 0.09375rem;
export const TopHat = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandPrimary,
    fontSize: Typescale.topHat,
    fontWeight: 500,
    lineHeight: 1.4,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.topHat
}))`
  /* if case exists use it otherwise set to uppercase */
  text-transform: ${props => (props.case ? props.case : 'uppercase')};
  letter-spacing: ${props =>
    props.letterSpacing ? props.letterSpacing : '.09375rem'};
`

// Action/Default
// font-size: 1.125rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.375rem; /* 122.222% */
export const Action = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandCode,
    fontSize: Typescale.action,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.2,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.action
}))``

// Action Large
// font-size: 1.25rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.375rem; /* 110% */
export const ActionLarge = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandCode,
    fontSize: Typescale.actionLarge,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.1,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.action
}))``

// Action Small
// font-size: 1rem;
// font-style: normal;
// font-weight: 400;
// line-height: 1.375rem; /* 137.5% */
export const ActionSmall = styled(TypographyComponent as any).attrs(props => ({
  as: props.as ? props.as : 'p',
  defaultStyles: {
    color: BrandColors.text,
    fontFamily: FontFamily.brandCode,
    fontSize: Typescale.actionSmall,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.375,
    textWrapBalance: false
  },
  fontSizeMobile: props.fontSizeMobile ? props.fontSizeMobile : Typescale.action
}))``

// TAILWIND COMPONENTS
// Define the props type
interface TypographyProps {
  children: React.ReactNode
  className?: string
  as?: keyof JSX.IntrinsicElements // Restrict 'as' to valid HTML elements
}

// TitleH1
export const TwTitleH1: React.FC<TypographyProps> = ({
  children,
  as: Component = 'h1',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[3.75rem] leading-[4.5rem] tracking-[-0.075rem] ${className}`}
  >
    {children}
  </Component>
)

// TitleH2
export const TwTitleH2: React.FC<TypographyProps> = ({
  children,
  as: Component = 'h2',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[2.25rem] leading-[1.1667] tracking-[-0.0225rem] ${className}`}
  >
    {children}
  </Component>
)

// TitleH3
export const TwTitleH3: React.FC<TypographyProps> = ({
  children,
  as: Component = 'h3',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1.875rem] leading-[2.25rem] tracking-[-0.01875rem] ${className}`}
  >
    {children}
  </Component>
)

// TitleH4
export const TwTitleH4: React.FC<TypographyProps> = ({
  children,
  as: Component = 'h4',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1.25rem] leading-[1.75rem] ${className}`}
  >
    {children}
  </Component>
)

// TitleH5
export const TwTitleH5: React.FC<TypographyProps> = ({
  children,
  as: Component = 'h5',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1.125rem] leading-[1.5rem] ${className}`}
  >
    {children}
  </Component>
)

// TitleH6
export const TwTitleH6: React.FC<TypographyProps> = ({
  children,
  as: Component = 'h6',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1rem] leading-[1.4375rem] ${className}`}
  >
    {children}
  </Component>
)

// Body1
export const TwBody1: React.FC<TypographyProps> = ({
  children,
  as: Component = 'p',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1.5rem] leading-[2.125rem] ${className}`}
  >
    {children}
  </Component>
)

// Body2
export const TwBody2: React.FC<TypographyProps> = ({
  children,
  as: Component = 'p',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1.25rem] leading-[1.75rem] ${className}`}
  >
    {children}
  </Component>
)

// Body3
export const TwBody3: React.FC<TypographyProps> = ({
  children,
  as: Component = 'p',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1rem] leading-[1.375rem] ${className}`}
  >
    {children}
  </Component>
)

// Caption
export const TwCaption: React.FC<TypographyProps> = ({
  children,
  as: Component = 'p',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[0.875rem] leading-[1.3125rem] ${className}`}
  >
    {children}
  </Component>
)

// CaptionSmall
export const TwCaptionSmall: React.FC<TypographyProps> = ({
  children,
  as: Component = 'p',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[0.6875rem] leading-[1.125rem] ${className}`}
  >
    {children}
  </Component>
)

// TopHat
export const TwTopHat: React.FC<
  TypographyProps & { caseTransform?: 'uppercase' | 'lowercase' | 'capitalize' }
> = ({
  children,
  as: Component = 'p',
  caseTransform = 'uppercase',
  className = ''
}) => (
  <Component
    className={`text-inherit font-medium text-[0.9375rem] leading-[1.3125rem] tracking-[0.09375rem] ${className}`}
    style={{ textTransform: caseTransform }}
  >
    {children}
  </Component>
)

// Action
export const TwAction: React.FC<TypographyProps> = ({
  children,
  as: Component = 'p',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1.125rem] leading-[1.375rem] ${className}`}
  >
    {children}
  </Component>
)

// ActionLarge
export const TwActionLarge: React.FC<TypographyProps> = ({
  children,
  as: Component = 'p',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1.25rem] leading-[1.375rem] ${className}`}
  >
    {children}
  </Component>
)

// ActionSmall
export const TwActionSmall: React.FC<TypographyProps> = ({
  children,
  as: Component = 'p',
  className = ''
}) => (
  <Component
    className={`text-inherit font-normal text-[1rem] leading-[1.375rem] ${className}`}
  >
    {children}
  </Component>
)
